import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple } from "@phosphor-icons/react";

const ALLOWED_EXTS = ["jpg", "jpeg", "png", "gif", "webp"];
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024; // 10MB hard cap
const COMPRESS_BYTES = 2 * 1024 * 1024; // 2MB trigger
const MAX_DIMENSION = 2000; // px trigger
const JPEG_QUALITY = 0.85;
const ALLOWED_MSG = "Only JPG, JPEG, PNG, GIF, or WEBP images up to 10MB are allowed.";

/**
 * Silently downscale & re-encode large images to JPEG before upload.
 * Returns a File. Returns the original file unchanged if compression
 * isn't needed or fails for any reason.
 */
async function maybeCompress(file) {
  const tooBig = file.size > COMPRESS_BYTES;
  // We can only decode rasters we can draw to canvas. JPEG/PNG/WEBP are fine;
  // animated GIFs would lose animation if we re-encode, so skip them.
  const decodable = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
  if (!decodable) return file;

  try {
    // imageOrientation: "from-image" tells the browser to apply EXIF
    // orientation tags during decode — phone photos shot in portrait will
    // be rotated upright before we draw them. Supported by Chrome, Edge,
    // Firefox, and Safari 15+. We fall back to default decode if a browser
    // rejects the option.
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bitmap = await createImageBitmap(file);
    }
    const needsResize = bitmap.width > MAX_DIMENSION || bitmap.height > MAX_DIMENSION;
    if (!tooBig && !needsResize) {
      bitmap.close?.();
      return file;
    }
    const scale = needsResize
      ? Math.min(MAX_DIMENSION / bitmap.width, MAX_DIMENSION / bitmap.height)
      : 1;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Flatten transparency on a white background (JPEG has no alpha)
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", JPEG_QUALITY)
    );
    if (!blob) return file;
    // Only swap if the compressed version is actually smaller.
    if (blob.size >= file.size) return file;
    const base = (file.name.replace(/\.[^.]+$/, "") || "photo");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

export default function PhotoUpload({ onUploaded, label = "Upload photo", testId = "photo-upload-input" }) {
  const [busy, setBusy] = useState(false);

  const validate = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_MIME.includes(file.type)) {
      return ALLOWED_MSG;
    }
    if (file.size > MAX_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      return `That file is ${mb}MB. ${ALLOWED_MSG}`;
    }
    if (file.size === 0) return "That file is empty.";
    return null;
  };

  const handleChange = async (e) => {
    const original = e.target.files?.[0];
    if (!original) return;
    // Validate the selected file first so we reject obvious bad types fast.
    const earlyErr = (() => {
      const ext = (original.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_MIME.includes(original.type)) return ALLOWED_MSG;
      return null;
    })();
    if (earlyErr) {
      toast.error(earlyErr);
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const file = await maybeCompress(original);
      const err = validate(file);
      if (err) {
        toast.error(err);
        return;
      }
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(data.storage_path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream border border-sage text-forest text-sm font-medium cursor-pointer hover:bg-gold/20 transition-colors" data-testid={`${testId}-label`}>
      <UploadSimple size={16} weight="bold" />
      {busy ? "Uploading…" : label}
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
        onChange={handleChange}
        className="hidden"
        disabled={busy}
        data-testid={testId}
      />
    </label>
  );
}
